import React, { useState } from "react";
import type {
  StepMap,
  StepProps,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.types";
import {
  FooterButton,
  FooterContainer,
  StepTitle,
  OptionsSection,
  CheckableOption,
  StepContent,
  OtherInput,
  WizardContentWrapper,
} from "@/components/onboarding/components/welcome-wizard/welcome-wizard.styles";
import { useAppStore } from "@/store";
import { observer } from "mobx-react-lite";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { posthogClientManager } from "@/manager/posthog-client";
import {
  MDSText,
  MDSTextVariant,
  MDSTextWeight,
  MDSButtonVariant,
  MDSIcon,
  MDSIconKind,
  MDSIconColor,
} from "@/mds";

interface ReferrerOption {
  id: string;
  title: string;
}

const ReferrerOptions: ReferrerOption[] = [
  { id: "linkedin", title: "LinkedIn" },
  { id: "x-twitter", title: "X (Twitter)" },
  { id: "youtube", title: "YouTube" },
  { id: "google-search", title: "Google Search" },
  { id: "ai-chat", title: "AI Chat (ChatGPT, Claude, Gemini)" },
  { id: "other", title: "Other" },
];

export const ReferrerStep: React.FC<StepProps> = observer(({ onNavToStep, onClose }) => {
  const { store } = useAppStore();
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [otherText, setOtherText] = useState<string>("");

  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
    if (optionId !== "other") {
      setOtherText("");
    }
  };

  const hasImportCtaStepNext = store.platformInfo.onboardingInfo.shouldShowImportCta;
  const hasMeetingSystemPermissionsStepNext =
    store.platformInfo.perDeviceOnboardingInfo.shouldShowMeetingOnboardingSystemPermissions;
  const hasVoiceSystemPermissionsStepNext =
    store.platformInfo.perDeviceOnboardingInfo.shouldShowVoiceOnboardingSystemPermissions;
  const hasAnyNextSteps =
    hasImportCtaStepNext ||
    hasMeetingSystemPermissionsStepNext ||
    hasVoiceSystemPermissionsStepNext;

  const handleContinue = async () => {
    if (!selectedOption) return;

    // Track step completion with user selection
    const selectedOptionTitle = ReferrerOptions.find(opt => opt.id === selectedOption)?.title;
    const properties = {
      selectedOption,
      selectedOptionTitle,
      otherText: selectedOption === "other" ? otherText.trim() : undefined,
    };
    trackEvent(TrackedEvent.ReferrerStepComplete, properties);

    // Set referrer data as PostHog person properties
    const posthogUserProperties = {
      referrer_source: selectedOption,
      referrer_source_label: selectedOptionTitle,
      referrer_other_text: selectedOption === "other" ? otherText.trim() : undefined,
      referrer_completed: true,
      referrer_completed_at: new Date().toISOString(),
    };

    const posthogReferrerOnceProperties = {
      initial_referrer_completed_at: new Date().toISOString(),
    };

    posthogClientManager.setPersonProperties(posthogUserProperties, posthogReferrerOnceProperties);

    onClose?.(["referrer"]);

    // First, check if we should show import CTA...
    if (hasImportCtaStepNext) {
      onNavToStep?.("app-selection");
      return;
    }

    // ...otherwise, we check if we should show meeting permissions steps
    if (hasMeetingSystemPermissionsStepNext) {
      onNavToStep?.("system-permissions-meeting-intro");
      return;
    }

    // ...otherwise, we check if we should show voice permissions steps
    if (hasVoiceSystemPermissionsStepNext) {
      onNavToStep?.("system-permissions-voice");
      return;
    }
  };

  const isContinueEnabled =
    selectedOption && (selectedOption !== "other" || otherText.trim().length > 0);

  return (
    <WizardContentWrapper>
      <StepTitle>Welcome to Mem</StepTitle>

      <StepContent>
        <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
          How did you hear about Mem?
        </MDSText>
        <OptionsSection>
          {ReferrerOptions.map(option => (
            <CheckableOption
              key={option.id}
              isSelected={selectedOption === option.id}
              onClick={() => handleOptionSelect(option.id)}
              isOtherOption={option.id === "other"}
              hasOtherText={option.id === "other" ? !!otherText : false}
            >
              {option.id === "other" && selectedOption === "other" ? (
                <OtherInput
                  placeholder="Please specify"
                  value={otherText}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOtherText(e.target.value)
                  }
                  onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  onKeyDown={(e: React.KeyboardEvent) => e.stopPropagation()}
                  onKeyUp={(e: React.KeyboardEvent) => e.stopPropagation()}
                  autoFocus
                />
              ) : (
                <MDSText variant={MDSTextVariant.ParagraphSm} weight={MDSTextWeight.Regular}>
                  {option.title}
                </MDSText>
              )}
              {selectedOption === option.id && (
                <MDSIcon
                  kind={MDSIconKind.CheckCircleSolid}
                  size={16}
                  color={MDSIconColor.Emphasis}
                />
              )}
            </CheckableOption>
          ))}
        </OptionsSection>
      </StepContent>

      <FooterContainer>
        <FooterButton
          variant={MDSButtonVariant.Contrast}
          onClick={handleContinue}
          isDisabled={!isContinueEnabled}
        >
          {hasAnyNextSteps ? "Next" : "Finish"}
        </FooterButton>
      </FooterContainer>
    </WizardContentWrapper>
  );
});

export const ReferrerStepsMap: StepMap = {
  referrer: {
    id: "referrer",
    group: "icp-rfi",
    render: props => <ReferrerStep {...props} />,
  },
} as const;
